import { IconCheck, IconFolder, IconMessageExclamation } from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import dayjs from "dayjs";
import { useState } from "react";
import {
  Alert, Badge, Button, Card, EmptyState, EstadoBadge, Group, Heading, PageHeader, Stack, Table, Tbody, Td, Text, Th, Thead, Tr,
} from "@/components/ui";
import { DocumentoRow } from "@/features/documentos/components/DocumentoRow";
import { useUserPermisos } from "@/lib/auth-store";
import { PERMISOS } from "@/lib/permisos";
import type { DocumentoVinculado } from "@/lib/types";
import { Route } from "@/routes/_authed-tw/solicitudes/$id/expediente";
import { useExpediente, useRevisarDocumento } from "../hooks/use-revision-documental";
import type { DocumentoEnExpediente, RevisionDeDocumento } from "../services/revision-documental.api";
import { EstadoRevisionBadge } from "./EstadoRevisionBadge";
import { VigenciaBadge } from "./VigenciaBadge";
import { ObservarDocumentoDialog } from "./ObservarDocumentoDialog";

/** Estados en los que el solicitante puede reemplazar cualquier documento reemplazable (espejo del backend). */
const ESTADOS_REEMPLAZO = new Set(["ProvisionalmenteListo", "AdjuntandoDocumentos", "RechazadaParaCorreccion", "Autorizada"]);

const ACCIONES_ACCESO: Record<string, string> = {
  Cargado: "Cargó",
  Reemplazado: "Reemplazó",
  Consultado: "Consultó",
  Eliminado: "Eliminó",
};

const fecha = (iso: string) => dayjs(iso).format("DD/MM/YYYY HH:mm");

const comoVinculado = (d: DocumentoEnExpediente): DocumentoVinculado => ({
  id: d.documentoId,
  documentoId: d.documentoId,
  categoria: d.categoria,
  nombreOriginal: d.nombreOriginal,
  vinculadoAt: "",
  tipoDocumento: d.tipoDocumento,
  numeroCfdi: d.numeroCfdi,
});

function HistorialRevisiones({ revisiones }: { revisiones: RevisionDeDocumento[] }) {
  if (revisiones.length === 0) return null;
  return (
    <ul className="space-y-1 border-l-2 border-border pl-3">
      {[...revisiones].reverse().map((r, i) => (
        <li key={`${r.version}-${r.revisadoAt}-${i}`}>
          <Text size="xs">
            <Text span fw={600}>
              v{r.version} · {r.resultado === "Aprobado" ? "Aprobado" : r.accion === "Bloquear" ? "Observado (bloquea)" : "Observado (devuelve)"}
            </Text>{" "}
            <Text span c="dimmed">
              · {r.revisorNombre ?? "Revisor"} · {fecha(r.revisadoAt)}
            </Text>
          </Text>
          {r.comentario ? (
            <Text size="xs" c="dimmed">
              {r.comentario}
            </Text>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/**
 * Expediente de la solicitud (Gestión Documental · spec §11): cada documento con su versión,
 * estado de revisión, observación vigente e historial de revisiones, y la bitácora de accesos.
 * El revisor aprueba u observa desde aquí; el solicitante reemplaza los documentos observados.
 */
export function ExpedientePage() {
  const { id } = Route.useParams();
  const { data: exp } = useExpediente(id);
  const permisos = useUserPermisos();
  const revisar = useRevisarDocumento(id);
  const [observando, setObservando] = useState<DocumentoEnExpediente | null>(null);

  if (!exp) return null;

  const enRevision = exp.estado === "EnRevisionDocumental";
  const gestiona = exp.esSolicitante && permisos.includes(PERMISOS.solicitudesGestionar);
  const enAutorizacion = exp.estado === "EnAutorizacionNivel1" || exp.estado === "EnAutorizacionNivelN";
  // Fase 4 · en autorización solo se reemplazan los vencidos (destraban la autorización bloqueada).
  const puedeReemplazar = (d: DocumentoEnExpediente) =>
    gestiona && d.esReemplazable &&
    (ESTADOS_REEMPLAZO.has(exp.estado) || (enRevision && d.estadoRevision === "Observado") || (enAutorizacion && d.vencido));
  const vencidos = exp.documentos.filter((d) => d.vencido);
  const observados = exp.documentos.filter((d) => d.estadoRevision === "Observado");
  const porRevisar = exp.documentos.filter((d) => d.estadoRevision === "PendienteDeRevision");

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 md:px-6">
      <PageHeader
        title={`Expediente · Solicitud ${exp.folio}`}
        description="Documentos de la solicitud con sus versiones, su revisión documental y los accesos registrados."
        breadcrumbs={[
          { label: "Inicio", href: "/dashboard" },
          { label: "Solicitudes", href: "/solicitudes" },
          { label: exp.folio, href: `/solicitudes/${id}` },
          { label: "Expediente" },
        ]}
        actions={<EstadoBadge estado={exp.estado} />}
      />

      <Stack gap="md">
        {enRevision && exp.esSolicitante ? (
          <Alert variant="info" title="En revisión documental">
            {porRevisar.length > 0
              ? `Se están revisando ${porRevisar.length} documento(s). Te avisaremos cuando terminen.`
              : "La revisión espera que reemplaces los documentos observados."}
            {observados.some((d) => d.accionObservacion === "Bloquear")
              ? " Reemplaza los documentos que bloquean la solicitud para que se vuelvan a revisar."
              : null}
          </Alert>
        ) : null}
        {vencidos.length > 0 ? (
          <Alert variant="warning" title={`${vencidos.length} documento(s) vencido(s)`}>
            {vencidos.map((d) => `${d.categoriaNombre}${d.tipoDocumento ? ` · ${d.tipoDocumento}` : ""}`).join(", ")}.
            {exp.esSolicitante ? " Reemplázalos por uno vigente con su nueva fecha de emisión." : null}
          </Alert>
        ) : null}
        {exp.estado === "RechazadaParaCorreccion" && observados.length > 0 && exp.esSolicitante ? (
          <Alert variant="warning" title="Documentos observados">
            Reemplaza los documentos observados y reenvía la solicitud desde el paso{" "}
            <Link to="/solicitudes/$id/documentos" params={{ id }} className="font-medium text-multiva-600 hover:underline">
              Documentos
            </Link>
            .
          </Alert>
        ) : null}

        <Card padding="lg">
          <Group gap="xs" className="mb-3">
            <IconFolder size={16} stroke={1.7} />
            <Heading order={5}>Documentos</Heading>
            <Badge size="xs" color="slate">
              {exp.documentos.length}
            </Badge>
          </Group>
          {exp.documentos.length === 0 ? (
            <EmptyState title="Sin documentos" description="La solicitud aún no tiene documentos." />
          ) : (
            <Stack gap="md">
              {exp.documentos.map((d) => {
                const accionable = enRevision && d.puedoRevisar && d.estadoRevision === "PendienteDeRevision";
                return (
                  <div key={d.documentoId} className="rounded-lg border border-border p-3">
                    <Group justify="space-between" align="flex-start" className="mb-2 flex-wrap">
                      <div className="min-w-0">
                        <Group gap="xs">
                          <Text size="sm" fw={600}>
                            {d.categoriaNombre}
                            {d.tipoDocumento ? ` · ${d.tipoDocumento}` : ""}
                          </Text>
                          <Badge size="xs" color="slate">
                            v{d.versionActual}
                          </Badge>
                          <EstadoRevisionBadge estado={d.estadoRevision} accion={d.accionObservacion} />
                          <VigenciaBadge manejaVigencia={d.manejaVigencia} fechaVencimiento={d.fechaVencimiento} vencido={d.vencido} />
                        </Group>
                        {d.fechaEmision ? (
                          <Text size="xs" c="dimmed">
                            Emitido el {dayjs(d.fechaEmision).format("DD/MM/YYYY")}
                          </Text>
                        ) : null}
                        {d.rolesRevisores.length > 0 ? (
                          <Text size="xs" c="dimmed">
                            Revisa: {d.rolesRevisores.join(", ")}
                          </Text>
                        ) : null}
                      </div>
                      {accionable ? (
                        <Group gap="xs">
                          <Button
                            size="xs"
                            className="bg-exito-600 hover:bg-exito-700"
                            leftSection={<IconCheck size={14} />}
                            loading={revisar.isPending && revisar.variables?.documentoId === d.documentoId && revisar.variables.body.aprobar}
                            disabled={d.aprobacionBloqueadaPorVencimiento}
                            title={d.aprobacionBloqueadaPorVencimiento ? "Está vencido y su configuración no permite aprobarlo · obsérvalo" : undefined}
                            onClick={() => revisar.mutate({ documentoId: d.documentoId, body: { aprobar: true } })}
                          >
                            Aprobar
                          </Button>
                          <Button size="xs" variant="default" leftSection={<IconMessageExclamation size={14} />} onClick={() => setObservando(d)}>
                            Observar
                          </Button>
                        </Group>
                      ) : null}
                    </Group>
                    <DocumentoRow
                      documento={comoVinculado(d)}
                      solicitudId={id}
                      puedeEliminar={false}
                      puedeReemplazar={puedeReemplazar(d)}
                      requiereFechaEmision={d.manejaVigencia}
                    />
                    {d.estadoRevision === "Observado" && d.comentarioRevision ? (
                      <Alert variant="warning" className="mt-2" title="Observación">
                        {d.comentarioRevision}
                      </Alert>
                    ) : null}
                    {d.revisiones.length > 0 ? (
                      <div className="mt-2">
                        <HistorialRevisiones revisiones={d.revisiones} />
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </Stack>
          )}
        </Card>

        <Card padding="lg">
          <Heading order={5} className="mb-3">
            Accesos
          </Heading>
          {exp.accesos.length === 0 ? (
            <Text size="sm" c="dimmed">
              Sin accesos registrados.
            </Text>
          ) : (
            <div className="overflow-x-auto">
              <Table verticalSpacing="xs" minWidth={640}>
                <Thead>
                  <Tr>
                    <Th>Fecha</Th>
                    <Th>Usuario</Th>
                    <Th>Acción</Th>
                    <Th>Documento</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {exp.accesos.map((a, i) => (
                    <Tr key={`${a.documentoId}-${a.ocurridoAt}-${i}`}>
                      <Td>{fecha(a.ocurridoAt)}</Td>
                      <Td>{a.usuarioNombre ?? "—"}</Td>
                      <Td>{ACCIONES_ACCESO[a.accion] ?? a.accion}</Td>
                      <Td>
                        <Text size="sm" truncate title={a.detalle ?? undefined}>
                          {a.nombreOriginal}
                        </Text>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </div>
          )}
        </Card>
      </Stack>

      <ObservarDocumentoDialog
        abierto={observando !== null}
        nombreDocumento={observando ? `${observando.categoriaNombre}${observando.tipoDocumento ? ` · ${observando.tipoDocumento}` : ""} · ${observando.nombreOriginal}` : ""}
        enviando={revisar.isPending}
        onCerrar={() => setObservando(null)}
        onConfirmar={(accion, comentario) =>
          observando &&
          revisar.mutate(
            { documentoId: observando.documentoId, body: { aprobar: false, accion, comentario } },
            { onSuccess: () => setObservando(null) }
          )
        }
      />
    </div>
  );
}
